/**
 * GT-FUEL-SOURCE-NEUTRAL-MANUAL-FUELING-TRANSACTION-BUILD-01
 * (GT-OPEN-120) — shared money contract.
 *
 * # Why this lives here
 *
 * Both the API (which validates and persists money) and the web app
 * (which renders it) need the SAME answer to one question: how many
 * minor units make one major unit of a given currency? Getting two
 * different answers is how a 1 234-fils Tunisian amount renders as
 * "12.34 TND". `@genius-tracker/config` is the one package both
 * `services/api` and `apps/web` already depend on, so the table lives
 * here rather than in a new root package or in `@genius-tracker/units`
 * — that package is deliberately about MEASUREMENT units, and its own
 * documentation records that money is not one of them.
 *
 * # Why it is a closed allow-list rather than "ISO-4217 support"
 *
 * ISO-4217 has ~180 entries and several irregular ones. Claiming
 * universal support while shipping a partial table would mean an
 * unlisted currency silently defaults to two decimals and stores a
 * wrong amount. So the list below is EXPLICIT and finite, and anything
 * outside it is REJECTED at the API boundary. Widening it is a
 * reviewed change: add the code with its exponent and a test.
 *
 * The set covers the euro/dollar-class two-decimal currencies the
 * product actually serves today, plus the three-decimal North-African
 * and Gulf currencies (the platform already operates in Morocco and
 * Tunisia) and the zero-decimal cases, because those three shapes are
 * exactly what a hardcoded `/ 100` gets wrong.
 *
 * # The invariant
 *
 *   major = minor / 10 ** exponent
 *
 * Every amount crossing the wire is an integer number of MINOR units
 * plus its currency. No binary floating point ever holds money.
 */

/**
 * ISO-4217 minor-unit exponents for every currency this platform
 * accepts. `0` = no minor unit (JPY), `2` = cents (EUR), `3` = mills
 * (TND, KWD, BHD, OMR, JOD).
 *
 * Source: ISO 4217:2015 Table A.1 (minor unit column).
 */
export const CURRENCY_MINOR_UNIT_EXPONENT: Readonly<Record<string, number>> = {
  // --- two-decimal (the overwhelming majority) ---
  AED: 2,
  AUD: 2,
  CAD: 2,
  CHF: 2,
  CNY: 2,
  CZK: 2,
  DKK: 2,
  EGP: 2,
  EUR: 2,
  GBP: 2,
  HUF: 2,
  ILS: 2,
  INR: 2,
  MAD: 2,
  MXN: 2,
  NOK: 2,
  NZD: 2,
  PLN: 2,
  QAR: 2,
  RON: 2,
  SAR: 2,
  SEK: 2,
  SGD: 2,
  TRY: 2,
  USD: 2,
  ZAR: 2,
  // --- three-decimal ---
  BHD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  // --- zero-decimal ---
  CLP: 0,
  ISK: 0,
  JPY: 0,
  KRW: 0,
  VND: 0,
  XOF: 0,
  XAF: 0,
};

/** Every currency code this platform accepts, sorted for stable output. */
export const SUPPORTED_CURRENCY_CODES: readonly string[] = Object.keys(
  CURRENCY_MINOR_UNIT_EXPONENT,
).sort();

/** ISO-4217 shape: exactly three uppercase letters. */
const ISO_4217_RE = /^[A-Z]{3}$/;

/**
 * `true` when `code` is a currency this platform is prepared to store
 * and render correctly. Anything else must be rejected — never
 * defaulted, because a wrong exponent silently corrupts the amount.
 */
export function isSupportedCurrency(code: unknown): code is string {
  return (
    typeof code === "string" &&
    ISO_4217_RE.test(code) &&
    Object.prototype.hasOwnProperty.call(CURRENCY_MINOR_UNIT_EXPONENT, code)
  );
}

/**
 * The minor-unit exponent for a supported currency.
 *
 * Throws for an unsupported code rather than assuming `2`. Callers on
 * the request path should test with {@link isSupportedCurrency} first
 * and return a validation error; reaching the throw means an unchecked
 * value got past the boundary, which is a bug worth surfacing loudly.
 */
export function currencyMinorUnitExponent(code: string): number {
  const exp = CURRENCY_MINOR_UNIT_EXPONENT[code];
  if (exp === undefined) {
    throw new Error(`unsupported_currency: ${code}`);
  }
  return exp;
}

/**
 * Convert an integer minor-unit amount into the major-unit decimal a
 * currency formatter expects (e.g. `123456` TND → `123.456`).
 *
 * The division happens once, here, against the currency's real
 * exponent — never as a hardcoded `/ 100` at a render site. The result
 * is a `number` because that is what `Intl.NumberFormat` consumes;
 * this is a PRESENTATION boundary only. Never feed the result back
 * into arithmetic that is then stored.
 */
export function minorToMajorAmount(minor: number, code: string): number {
  return minor / 10 ** currencyMinorUnitExponent(code);
}

/**
 * Format an integer minor-unit amount for display under a locale.
 *
 * Falls back to `"<amount> <CODE>"` when the runtime's Intl data does
 * not know the currency, so a rendering surface degrades to a truthful
 * string rather than throwing inside a component tree.
 */
export function formatMinorAmount(minor: number, code: string, locale: string): string {
  const major = minorToMajorAmount(minor, code);
  const digits = currencyMinorUnitExponent(code);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(major);
  } catch {
    return `${major.toFixed(digits)} ${code}`;
  }
}
